<template>
  <div class="container waybills-container">
    <base-nav title="Путевые листы">
      <base-primary-button
        icon="addSquare"
        @click="openCreateForm"
      >
        Создать
      </base-primary-button>
    </base-nav>

    <FilterBlock
      ref="filterBlock"
      :search-fields="searchFields"
      :initial-filters="appliedFilters"
      target-type="Waybill"
      @filter-change="handleFilterChange"
    />

    <MetaResultCount
      v-if="!waybillStore.dataIsLoading && waybillStore.waybills.length > 0"
      :pagination-meta="waybillStore.pagination"
    />

    <TableToolbar
      v-if="!waybillStore.dataIsLoading && waybillStore.waybills.length > 0"
      :show-settings="true"
      :actions="dropdownItems"
      @open-settings="showSettingsModal = true"
    />

    <base-loader v-if="waybillStore.dataIsLoading" />

    <WaybillTable
      v-else-if="waybillStore.waybills.length > 0"
      ref="waybillTable"
      @selection-change="onSelectionChange"
    />

    <div class="no-data" v-else>
      Путевые листы не найдены
    </div>

    <WaybillForm
      v-if="showForm"
      @close="closeForm"
      @waybill-created="closeForm"
    />

    <Teleport to="body">
      <TableSettingsModal
        v-if="showSettingsModal"
        resource-model="Waybill"
        record-type="Waybill"
        table-name="waybills"
        @close="showSettingsModal = false"
        @config-saved="handleConfigSaved"
      />

      <BaseDialog
        :visible="showDeleteDialog"
        title="Удаление путевых листов"
        :message="deleteDialogMessage"
        :actions="[
          { key: 'confirm', label: 'Удалить', type: 'danger' },
          { key: 'close', label: 'Отмена', type: 'secondary' },
        ]"
        @action="handleDeleteAction"
      />
    </Teleport>
  </div>
</template>

<script>
import FilterBlock from "@/components/Filter/FilterBlock.vue";
import MetaResultCount from "@/components/Meta/MetaResultCount.vue";
import TableSettingsModal from "@/components/TableSettingsModal.vue";
import TableToolbar from "@/components/Table/TableToolbar.vue";
import WaybillTable from "@/components/Waybill/WaybillTable.vue";
import WaybillForm from "@/components/Waybill/WaybillForm.vue";

import rawSearchFields from "@/config/filters/waybill/waybills.json";

import { useWaybillStore } from "@/stores/waybillStore";
import { useNotificationStore } from "@/stores/notificationStore";
import {
  filtersToApiParams,
  getInitialFilters,
  getCurrentPageFromUrl,
  updateUrlWithFilters,
  clearUrlFilters,
} from "@/utils/filterUtils";

export default {
  name: "WaybillsPage",

  components: {
    FilterBlock,
    MetaResultCount,
    TableSettingsModal,
    TableToolbar,
    WaybillTable,
    WaybillForm,
  },

  data() {
    return {
      showForm: false,
      showSettingsModal: false,
      showDeleteDialog: false,
      selectedWaybills: [],

      appliedFilters: {
        searchValues: {},
        showInactive: false,
      },
    };
  },

  computed: {
    waybillStore() {
      return useWaybillStore();
    },

    notificationStore() {
      return useNotificationStore();
    },

    dropdownItems() {
      return [
        {
          id: "delete",
          label: "Удалить",
          isDanger: true,
          action: this.openDeleteDialog,
        },
      ];
    },

    searchFields() {
      return rawSearchFields;
    },

    deleteDialogMessage() {
      const count = this.selectedWaybills.length;
      if (count === 1) {
        const waybill = this.selectedWaybills[0];
        return `Удалить путевой лист ${waybill.number || '#' + waybill.id}?`;
      }

      return `Удалить выбранные путевые листы: ${count}?`;
    },
  },

  watch: {
    "$route.query": {
      immediate: false,
      handler() {
        this.updateFiltersFromUrl();
        this.fetchWaybills();
      },
    },
  },

  methods: {
    openCreateForm() {
      this.showForm = true;
    },

    closeForm() {
      this.showForm = false;
    },

    openDeleteDialog() {
      if (this.selectedWaybills.length === 0) {
        this.notificationStore.showWarning(
          "Выберите хотя бы один путевой лист"
        );
        return;
      }

      this.showDeleteDialog = true;
    },

    async handleDeleteAction(actionKey) {
      this.showDeleteDialog = false;
      if (actionKey !== "confirm") return;

      const page = getCurrentPageFromUrl(this.$route);
      const params = {
        page,
        ...filtersToApiParams(this.appliedFilters),
      };

      await this.waybillStore.deleteWaybills(
        this.selectedWaybills,
        params
      );
      this.selectedWaybills = [];
    },

    onSelectionChange(selected) {
      this.selectedWaybills = selected;
    },

    handleConfigSaved() {
      if (this.$refs.waybillTable) {
        this.$refs.waybillTable.loadTableConfig();
        this.fetchWaybills();
      }
    },

    async fetchWaybills() {
      const page = getCurrentPageFromUrl(this.$route);
      const params = {
        page,
        ...filtersToApiParams(this.appliedFilters),
      };
      await this.waybillStore.fetchWaybills(params);
      this.selectedWaybills = [];
    },

    updateFiltersFromUrl() {
      this.appliedFilters = getInitialFilters(
        this.$route,
        this.searchFields,
        { searchValues: {}, showInactive: false }
      );
    },

    handleFilterChange(filters) {
      this.appliedFilters = { ...filters };

      const hasValues = Object.values(filters.searchValues).some(
        (v) => v !== null && v !== undefined && v !== ""
      );

      if (hasValues || filters.showInactive) {
        updateUrlWithFilters(this.$router, filters, 1);
      } else {
        clearUrlFilters(this.$router);
      }

      this.selectedWaybills = [];
    },
  },

  async mounted() {
    const storedFilters =
      await this.$refs.filterBlock?.loadStoredFilter();

    if (storedFilters) {
      this.appliedFilters = storedFilters;
      updateUrlWithFilters(this.$router, storedFilters, 1);
    }

    await this.fetchWaybills();
  },
};
</script>
